"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.staffDiscountAppliedResponseSchema = exports.staffDiscountOfferResponseSchema = exports.staffDiscountOfferSchema = exports.opBillDraftResponseSchema = exports.opBillDraftLineResponseSchema = exports.opBillCreditBalanceSchema = exports.opBillCreditLedgerResponseSchema = exports.opBillCreditLedgerEntrySchema = exports.priceBillResponseSchema = exports.pricedLineResponseSchema = exports.opBillDoctorRevenueSchema = exports.opBillDepartmentRevenueSchema = exports.opBillTopServiceSchema = exports.opBillHourlyPointSchema = exports.opBillPaymentMixSliceSchema = exports.opBillRevenueTrendPointSchema = exports.opBillDashboardKpisResponseSchema = exports.billDetailResponseSchema = exports.billDetailPaymentResponseSchema = exports.patientInvoiceContactResponseSchema = exports.gstSummaryRowResponseSchema = exports.billDetailLineResponseSchema = exports.billRegisterPageResponseSchema = exports.billRegisterTotalsResponseSchema = exports.billRegisterRowResponseSchema = exports.opBillTodaysAppointmentResponseSchema = exports.opBillDoctorOptionResponseSchema = exports.opBillAccountHeadResponseSchema = exports.chargeSearchResultResponseSchema = exports.billingContextResponseSchema = void 0;
/**
 * Response contracts for the OP Billing Workstation — ADR-062 Phase 4.
 *
 * WHY THESE EXIST, AND WHY ADDING THEM IS A DEFECT FIX RATHER THAN A REDESIGN
 *
 * The Gate 17 remediation registered every OP Billing endpoint in OpenAPI but
 * documented response bodies loosely (`envelope(unknown)`), matching the
 * precedent Financial, Patient and Clinical had already set. That was tolerable
 * while the only consumers were backend tests. It stops being tolerable the
 * moment a GENERATED CLIENT exists: a client whose response type is `unknown`
 * forces every caller to assert a shape, and asserting a shape in the browser
 * means redeclaring the DTO there — precisely the duplication Sprint 1's
 * criterion A8 forbids and `ENGINEERING-PRINCIPLES.md` §4 exists to prevent.
 *
 * The shapes are unchanged: this PUBLISHES what was already frozen, it does not
 * reshape it. That is why it is permitted under the backend baseline policy
 * (§8) as a defect fix surfaced by frontend work.
 *
 * ONE DECLARATION, NOT TWO THAT AGREE. Each shape is declared as a Zod schema
 * and its TypeScript type is `z.infer`red from it. Declaring an interface
 * alongside a schema would be two artifacts that must be kept in step by hand,
 * which is the same drift this file was created to stop — one level down.
 *
 * Shapes are published WHEN A REAL CALLER APPEARS, never ahead of one —
 * publishing all 22 up front would be guessing at what consumers need. Sprint 1
 * consumed two (billing context, charge search); Sprint 2's patient summary
 * consumes the register, so that one is published here now. The rest still
 * document `unknown`.
 */
const zod_1 = require("zod");
/**
 * Session configuration — the first call every workstation makes.
 *
 * Note what is NOT here: no draft, no invoice, no payment, no mutable state of
 * any kind (Billing Context Rule 2). The context describes WHERE and HOW you are
 * billing, never WHAT you are billing.
 */
exports.billingContextResponseSchema = zod_1.z.object({
    /** Deterministic digest of everything below; changes exactly when the context does. */
    version: zod_1.z.string(),
    cashier: zod_1.z.object({ userId: zod_1.z.string(), name: zod_1.z.string() }).nullable(),
    branch: zod_1.z.object({ branchId: zod_1.z.string(), code: zod_1.z.string(), name: zod_1.z.string() }).nullable(),
    /**
     * The supplier identity a GST tax invoice is legally required to carry.
     *
     * Lives on the CONTEXT rather than on each bill because it is session
     * configuration, not transaction data — it is the same for every bill printed
     * at this counter, and putting it on the invoice would repeat it per row.
     *
     * Sourced from `Hospital`, not `Branch`: GSTIN is registered to the legal
     * entity, and the branch rows carry no address. Before this existed the print
     * path cast a `gstin` off `branch` — a field that has never been on it — so
     * every printed bill had an empty letterhead.
     */
    hospital: zod_1.z
        .object({
        legalName: zod_1.z.string(),
        displayName: zod_1.z.string(),
        gstin: zod_1.z.string().nullable(),
        pan: zod_1.z.string().nullable(),
        registrationNumber: zod_1.z.string().nullable(),
        phone: zod_1.z.string().nullable(),
        email: zod_1.z.string().nullable(),
        /** Pre-assembled, so every consumer prints one address rather than joining five nullable columns its own way. */
        addressLines: zod_1.z.array(zod_1.z.string()),
        /** Place of supply comparison for the CGST+SGST vs IGST split. */
        state: zod_1.z.string().nullable(),
    })
        .nullable(),
    counter: zod_1.z.object({ counterId: zod_1.z.string(), code: zod_1.z.string(), name: zod_1.z.string() }).nullable(),
    /** The OPEN session at that counter. Null means "no session — cash cannot be taken". */
    cashSession: zod_1.z
        .object({
        cashSessionId: zod_1.z.string(),
        openingBalancePaise: zod_1.z.number().int(),
        openedAt: zod_1.z.string(),
        status: zod_1.z.string(),
        /**
         * ADR-086. The session's own business date and owning cashier, which
         * `billing-context.queries.ts` already returns — the query was changed
         * without this schema, so the contract described four fields while the
         * endpoint sent six and `tsc` rejected the surplus.
         *
         * Both belong on the wire: a session's business date can differ from
         * today (a night shift that opened before midnight), and the cashier id
         * is what lets the workstation tell "no session" apart from "someone
         * else's session at this counter".
         */
        businessDate: zod_1.z.string(),
        cashierUserId: zod_1.z.string(),
    })
        .nullable(),
    financialYear: zod_1.z
        .object({ financialYearId: zod_1.z.string(), label: zod_1.z.string(), startDate: zod_1.z.string(), endDate: zod_1.z.string() })
        .nullable(),
    accountingPeriod: zod_1.z.object({ accountingPeriodId: zod_1.z.string(), status: zod_1.z.string() }).nullable(),
    paymentModes: zod_1.z.array(zod_1.z.object({ paymentModeId: zod_1.z.string(), code: zod_1.z.string(), name: zod_1.z.string() })),
    /** Straight from the caller's verified token — the context does not compute authority. */
    permissions: zod_1.z.array(zod_1.z.string()),
    policies: zod_1.z.object({
        roundOffMode: zod_1.z.string(),
        roundOffPrecisionPaise: zod_1.z.number().int(),
        advancePolicy: zod_1.z.string(),
        fullRefundPolicy: zod_1.z.string(),
        maxDiscountPercentWithoutApproval: zod_1.z.number().nullable(),
        duplicatePrintRequiresReason: zod_1.z.boolean(),
        autoPrintOnPost: zod_1.z.boolean(),
        draftRetentionDays: zod_1.z.number().int(),
        defaultPlaceOfSupply: zod_1.z.string().nullable(),
    }),
    /** Held bills at this counter block its cash session from closing (§C7). */
    heldBillCount: zod_1.z.number().int(),
});
/**
 * One charge-search hit — metadata, plus the C1 list price.
 *
 * `standardPricePaise` is the ONE financial field here and its own note below
 * states exactly what it is: the charge master's stored C1 (`STANDARD`) tier,
 * read as a column. It is the LIST price, not the resolved one — a payer
 * tariff, package allowance or contract rate can still move it, and the figure
 * that reaches the invoice comes from `RateResolutionService` after the charge
 * is selected.
 *
 * Gate 16 still holds: a read model may not REACH a pricing service, and
 * `charge-search.queries.ts` calls none. The frontend fitness test still fails
 * the build if the browser derives a figure of its own.
 */
exports.chargeSearchResultResponseSchema = zod_1.z.object({
    chargeId: zod_1.z.string(),
    chargeCode: zod_1.z.string(),
    name: zod_1.z.string(),
    shortName: zod_1.z.string().nullable(),
    departmentId: zod_1.z.string().nullable(),
    departmentName: zod_1.z.string().nullable(),
    categoryId: zod_1.z.string().nullable(),
    categoryName: zod_1.z.string().nullable(),
    isActive: zod_1.z.boolean(),
    isBillable: zod_1.z.boolean(),
    /** How often this charge was billed recently — operational metadata, never a price. */
    recentUseCount: zod_1.z.number().int(),
    /**
     * ADR-083 identity and classification, when the charge is a Service Master
     * service. Null for every charge created before ADR-083 or by the legacy
     * screen — a catalogue holds both, and the counter must show what each row
     * actually has rather than inventing an identifier for the ones that have none.
     *
     * STILL METADATA, NOT PRICING. The rule above is untouched: an incode says
     * which income head a service belongs to, never what it costs.
     */
    servCode: zod_1.z.string().nullable(),
    servNumber: zod_1.z.string().nullable(),
    /** `CFE369` — the identifier an operator actually says out loud. */
    identifier: zod_1.z.string().nullable(),
    incode: zod_1.z.string().nullable(),
    revenueSubHead: zod_1.z.string().nullable(),
    /** The sub head's stable code. `revenueSubHead` is a caption that can be
     *  renamed; this is what a revenue report groups by. */
    revenueSubHeadCode: zod_1.z.string().nullable(),
    /** Which account head this charge is filed under — the Account Heads tab's key. */
    accountHeadId: zod_1.z.string().nullable(),
    /**
     * THE C1 (STANDARD) LIST PRICE, in paise. Null when the charge has no active
     * `STANDARD` price version — which also means it cannot be billed at all.
     *
     * ADDED AT THE CLIENT'S EXPLICIT DIRECTION, and it is the one exception to
     * this response's "no financial figure" rule, so what it is and is NOT must
     * be stated precisely:
     *
     *   - It is the charge master's stored C1 tier — `ChargePriceVersion` where
     *     `rateType = STANDARD` and `status = ACTIVE`, read as a column. C2/C3/C4
     *     (SEMI_PRIVATE/PRIVATE/DELUXE) are the ward tiers and are deliberately
     *     NOT returned: OP billing resolves at C1, so showing the others at an OP
     *     counter would offer three prices the bill will not use.
     *   - It is NOT the resolved price. A payer tariff, a package allowance, a
     *     corporate rate or a promotion can still move it, and the bill's figure
     *     comes from `RateResolutionService` after the charge is selected. The UI
     *     must label it as the standard rate rather than as the amount payable.
     *
     * Gate 16 is untouched: this is a table read, not a rate resolution — the
     * query imports no pricing service and calls no resolver.
     */
    standardPricePaise: zod_1.z.number().int().nullable(),
    /**
     * THE LIST PRICE AT EVERY TIER, in paise. Null per tier means no ACTIVE price
     * version there — not that the service is free.
     *
     * WHY THE C1-ONLY RULE ABOVE NO LONGER HOLDS — desk, 2026-09-05: "all search
     * of service line item under the billing pages, price is showing only C1 but
     * need all category prices C1,C2,C3,C4 all type service".
     *
     * "OP billing resolves at C1" was true of the OP counter and false of the
     * panel, which the IP workstation also uses — there the patient IS admitted
     * and the ward tier IS the price. It broke hardest on the 999 ward rooms:
     * priced at a single ward tier with no C1 by design, they showed "No price"
     * beside a room with a real tariff, because C1 is the one column they leave
     * empty.
     *
     * `standardPricePaise` above is unchanged and still means C1 — the OP counter
     * reads it, and redefining it would change what that screen prices at.
     *
     * Gate 16 is untouched: four column reads in one grouped query, no resolver.
     * These remain LIST prices; the bill's figure still comes from
     * `RateResolutionService` after the charge is selected.
     */
    tierPricesPaise: zod_1.z.object({
        C1: zod_1.z.number().int().nullable(),
        C2: zod_1.z.number().int().nullable(),
        C3: zod_1.z.number().int().nullable(),
        C4: zod_1.z.number().int().nullable(),
    }),
});
/**
 * One account head, as the billing counter sees it.
 *
 * WHY IT CARRIES A SERVICE COUNT AND NO PRICE. An account head is not itself
 * billable — it has no price and never will, because it is a classification, not
 * a service. What goes on a bill is a SERVICE FILED UNDER it. So the number that
 * matters at the counter is "how many billable services does this head have?":
 * a head showing 0 is the honest answer to "why can I not bill this?", and it is
 * the number that sends an administrator to the Service master rather than
 * leaving a biller clicking a row that can never do anything.
 *
 * The same no-price rule as `chargeSearchResultResponseSchema` applies, for the
 * same reason — see that schema's note.
 */
exports.opBillAccountHeadResponseSchema = zod_1.z.object({
    accountHeadId: zod_1.z.string(),
    /** "441.00". Null on a head created before incodes were allocated. */
    incode: zod_1.z.string().nullable(),
    departmentId: zod_1.z.string().nullable(),
    departmentName: zod_1.z.string().nullable(),
    revenueSubHead: zod_1.z.string().nullable(),
    revenueSubHeadCode: zod_1.z.string().nullable(),
    /** The ledger this head's revenue is credited to — shown, never chosen here. */
    revenueLedgerCode: zod_1.z.string().nullable(),
    revenueLedgerName: zod_1.z.string().nullable(),
    /** Services filed under this head that are active AND billable. May be 0. */
    billableServiceCount: zod_1.z.number().int(),
});
/**
 * One doctor a line can be attributed to.
 *
 * `userId` IS THE ONE THAT GOES ON THE INVOICE. `FinInvoiceLine.doctorId` and
 * `FinInvoice.doctorId` are foreign keys to `users`, not to `doctors`; a
 * `Doctor.id` here matches zero rows and fails silently as a blank column on
 * every revenue report. `doctorId` is carried alongside for display and
 * drill-through only.
 */
exports.opBillDoctorOptionResponseSchema = zod_1.z.object({
    userId: zod_1.z.string(),
    doctorId: zod_1.z.string(),
    doctorCode: zod_1.z.string(),
    name: zod_1.z.string(),
    /** True when this is the doctor's primary department rather than a secondary specialty. */
    isPrimary: zod_1.z.boolean(),
});
/**
 * One of today's appointments for the patient at the counter — what the New
 * Bill screen recommends its doctor from.
 *
 * `doctorUserId`, again, is the `users` id: an appointment's `doctorId` is a
 * `users` FK, and it is directly assignable to the bill's doctor without
 * translation. `doctorName` is nullable because the Doctor master row is
 * joined only for display and a user with no such row still bills correctly.
 */
exports.opBillTodaysAppointmentResponseSchema = zod_1.z.object({
    appointmentId: zod_1.z.string(),
    doctorUserId: zod_1.z.string(),
    doctorName: zod_1.z.string().nullable(),
    departmentName: zod_1.z.string().nullable(),
    scheduledAt: zod_1.z.string(),
    slotNumber: zod_1.z.number().int().nullable(),
    status: zod_1.z.string(),
});
/**
 * One row of the bill register.
 *
 * EVERY FIGURE HERE IS SERVER-DECIDED, and the browser renders them without
 * adding, subtracting or totalling any of them (ADR-062 §6A.10). In particular
 * `amountOutstandingPaise` is per-bill: there is deliberately no patient-level
 * total in this contract, because summing these in a client would be exactly the
 * money calculation the rule forbids.
 *
 * Financial attributes are snapshots taken at posting; `patientName`,
 * `doctorName` and `departmentName` are DISPLAY LABELS read from current masters
 * — so a renamed charge never alters a posted bill, while a renamed doctor
 * updates everywhere (ADR-065 §1.5).
 */
exports.billRegisterRowResponseSchema = zod_1.z.object({
    invoiceId: zod_1.z.string(),
    invoiceNumber: zod_1.z.string(),
    invoiceDate: zod_1.z.string(),
    patientId: zod_1.z.string(),
    patientName: zod_1.z.string().nullable(),
    uhid: zod_1.z.string().nullable(),
    doctorId: zod_1.z.string().nullable(),
    doctorName: zod_1.z.string().nullable(),
    departmentId: zod_1.z.string().nullable(),
    departmentName: zod_1.z.string().nullable(),
    grossAmountPaise: zod_1.z.number().int(),
    discountAmountPaise: zod_1.z.number().int(),
    taxAmountPaise: zod_1.z.number().int(),
    roundOffAmountPaise: zod_1.z.number().int(),
    netAmountPaise: zod_1.z.number().int(),
    amountPaidPaise: zod_1.z.number().int(),
    amountOutstandingPaise: zod_1.z.number().int(),
    status: zod_1.z.string(),
    refundState: zod_1.z.string(),
    printCount: zod_1.z.number().int(),
    cashierUserId: zod_1.z.string().nullable(),
    /** WHO raised it. An id alone is not something a register can show anyone. */
    cashierName: zod_1.z.string().nullable(),
    counterId: zod_1.z.string().nullable(),
    /** WHICH counter took it — a shift is reconciled counter by counter. */
    counterName: zod_1.z.string().nullable(),
    /**
     * WHICH ACT produced the document: a bill raised at the OP counter, or an IP
     * advance taken at the same counter on a receipt from the same number series.
     * The register lists both, so it has to say which.
     */
    source: zod_1.z.enum(['OP_COUNTER', 'IP_ADVANCE']),
    /** The stay an advance belongs to — the other half of its receipt's URL. Null for a bill. */
    sourceAdmissionId: zod_1.z.string().nullable(),
    /** Full timestamp. `invoiceDate` is a DATE and cannot say when in the day. */
    createdAt: zod_1.z.string(),
    /** True when this bill has no `patientId` — a walk-in, priced against a name rather than a patient record. */
    isWalkIn: zod_1.z.boolean(),
    /**
     * WALK-IN ONLY (desk, 2026-09-25) — null for a patient-identified bill and
     * for an IP advance. See `FinInvoice.walkInMobile`/`walkInGender`/
     * `walkInAgeYears`.
     */
    walkInMobile: zod_1.z.string().nullable(),
    walkInGender: zod_1.z.string().nullable(),
    walkInAgeYears: zod_1.z.number().int().nullable(),
    /** The allotted Private Patient Register number. See `FinInvoice.walkInNumber`. */
    walkInNumber: zod_1.z.string().nullable(),
});
/**
 * A page of the register. Cursor-based, not offset: the underlying table takes
 * concurrent inserts all day, and offset paging under concurrent inserts both
 * duplicates and skips rows mid-page.
 */
/**
 * The register's footer, over the WHOLE filtered set rather than the page.
 *
 * Advances are cash, not revenue (2026-08-26): `grossPaise`/`netPaise` are
 * invoices only, `collectedPaise` includes advances, and
 * `advanceCollectedPaise` states the difference on the same row so nobody has
 * to derive it. The two counts stay separate — an advance is not a bill.
 */
exports.billRegisterTotalsResponseSchema = zod_1.z.object({
    billCount: zod_1.z.number().int(),
    advanceCount: zod_1.z.number().int(),
    grossPaise: zod_1.z.number().int(),
    discountPaise: zod_1.z.number().int(),
    taxPaise: zod_1.z.number().int(),
    netPaise: zod_1.z.number().int(),
    collectedPaise: zod_1.z.number().int(),
    advanceCollectedPaise: zod_1.z.number().int(),
    outstandingPaise: zod_1.z.number().int(),
    /**
     * HOW THE MONEY ARRIVED, AND WHAT IS STILL OWED (desk, 2026-09-08).
     *
     * Sent on the WHOLE-SET totals only, never on `pageTotals`: a bank-wise
     * breakdown of the fifty rows on screen answers a question nobody is asking,
     * and would sit under a heading that reads as the day's reconciliation.
     *
     * `bankSplit` EXCLUDES CASH — it reaches a drawer, not an account, and it is
     * already `cashPaise` above. Listing it as "(no bank)" would make the bank
     * list look like it double-counts the collection.
     *
     * The credits are OUTSTANDING, not a payment mode: all nine configured modes
     * are money that arrived, so a bill taken on credit is one that was raised
     * and not settled. Company = a named payer; staff = a patient flagged
     * `isEmployeeDependent`. NOT the staff concession, which is a discount and
     * already inside `discountPaise`.
     *
     * OPTIONAL ON THE WIRE, for the same reason `pageTotals` is: a browser cached
     * against the previous release renders the register rather than failing
     * validation on a field it has never seen.
     */
    settlement: zod_1.z
        .object({
        cashPaise: zod_1.z.number().int(),
        cardPaise: zod_1.z.number().int(),
        upiPaise: zod_1.z.number().int(),
        chequePaise: zod_1.z.number().int(),
        bankTransferPaise: zod_1.z.number().int(),
        otherPaise: zod_1.z.number().int(),
        bankSplit: zod_1.z.array(zod_1.z.object({
            bankAccountId: zod_1.z.string(),
            bankName: zod_1.z.string(),
            accountNumber: zod_1.z.string().nullable(),
            amountPaise: zod_1.z.number().int(),
        })),
        companyCreditPaise: zod_1.z.number().int(),
        staffCreditPaise: zod_1.z.number().int(),
    })
        .optional(),
});
exports.billRegisterPageResponseSchema = zod_1.z.object({
    data: zod_1.z.array(exports.billRegisterRowResponseSchema),
    /** Composite `(invoiceDate, id)` — a date-only cursor loops, since hundreds of bills share a date. */
    nextCursor: zod_1.z.string().nullable(),
    hasMore: zod_1.z.boolean(),
    totals: exports.billRegisterTotalsResponseSchema,
    /**
     * The same figures over THIS PAGE's rows (2026-08-26, desk: "total should
     * show as per line below").
     *
     * Both are sent, and both are needed: `pageTotals` ties to the lines the
     * operator can see and add up, `totals` ties to the day's reconciliation and
     * to the Collection card. Server-computed for the same reason every other
     * figure here is — a page summed in the browser is a money calculation in a
     * screen, and this register's own header names it as the most tempting place
     * in the application to put one.
     *
     * OPTIONAL ON THE WIRE, so a browser cached against the previous release
     * renders the register rather than failing validation on a field it has never
     * seen. The server always sends it.
     */
    pageTotals: exports.billRegisterTotalsResponseSchema.optional(),
});
/**
 * One line of a posted bill, as the register's detail view reports it.
 *
 * READ FROM THE LINE'S OWN SNAPSHOT COLUMNS, never from a join to ChargeMaster
 * — `chargeCode` and `description` are `chargeCodeSnapshot`/`chargeNameSnapshot`
 * (ADR-065). A charge renamed or re-priced after posting must not alter what a
 * historical bill says it billed, which is the whole reason those columns exist.
 *
 * `taxPercent` is likewise the rate that WAS applied, not the rate configured
 * today. A GST change next quarter cannot retroactively rewrite a filed return.
 */
exports.billDetailLineResponseSchema = zod_1.z.object({
    lineId: zod_1.z.string(),
    chargeCode: zod_1.z.string().nullable(),
    description: zod_1.z.string(),
    quantity: zod_1.z.number().nullable(),
    unitRatePaise: zod_1.z.number().int().nullable(),
    discountAmountPaise: zod_1.z.number().int(),
    /** Gross less discount — the value tax was charged ON. Sent so the invoice can print a Taxable column without the browser subtracting. */
    taxableAmountPaise: zod_1.z.number().int(),
    taxPercent: zod_1.z.number().nullable(),
    taxAmountPaise: zod_1.z.number().int(),
    netAmountPaise: zod_1.z.number().int(),
    hsnSacCode: zod_1.z.string().nullable(),
    /** Provenance from `RateResolutionService` — which rule produced this rate (ADR-045). */
    rateSource: zod_1.z.string().nullable(),
    rateType: zod_1.z.string().nullable(),
    /**
     * WHAT THE COUNTER TYPED IN THIS LINE'S REMARKS BOX — desk, 2026-08-31:
     * "whatever remarks enters in each line should be saved and showed in print
     * view and print".
     *
     * The grid has had the box since it was built and the value went nowhere: it
     * reached the server on the draft, sat in the payload JSON, and was dropped
     * at post because neither the priced line nor `fin_invoice_lines` carried it.
     * Now stored on the line and returned here, so the View and the printed sheet
     * show the same words the operator wrote.
     *
     * Null on every line posted before that date, and deliberately not
     * backfilled — a remark is what somebody typed, and inventing one would be
     * fabricating a record.
     */
    remarks: zod_1.z.string().nullable(),
    /**
     * POSTING-TIME CLASSIFICATION, PRINTED AHEAD OF THE SERVICE NAME — desk,
     * 2026-09-03 / widened 2026-09-10. The SERVER already resolved and returned
     * these (`OpBillRegisterQueries.detail`); this schema simply never declared
     * them, so the TYPE `BillDetailLineDto` had no field for a caller to read —
     * which is why `BillDetailDialog`'s "View" (the same dialog this Appointments
     * receipt preview reuses) kept printing "CONS FEE - OP" with no department or
     * doctor line under it, THREE TIMES, while the print path (which builds its
     * own DTO) got it right every time. One schema for the two paths that render
     * the same `TaxInvoiceDocument`, so they cannot drift apart again.
     */
    departmentName: zod_1.z.string().nullable(),
    revenueSubHead: zod_1.z.string().nullable(),
    /** The line's own clinician — `User`, not `Doctor`. */
    doctorName: zod_1.z.string().nullable(),
    /** Which context the sheet may show for this line — see `line-presentation.util.ts`. */
    isAccountHeadLine: zod_1.z.boolean(),
    isConsultation: zod_1.z.boolean(),
});
/**
 * One row of the rate-wise GST breakup a tax invoice must print (CGST Rules,
 * r.46) — grouped by (HSN/SAC, rate) and totalled on the SERVER.
 *
 * Not derived in the browser, and that is enforced rather than intended: the
 * Sprint 1/2 acceptance fences fail the build on money arithmetic in a feature
 * file, and they caught the first version of this table being summed in the
 * print component. A GST return is prepared from these figures, so a browser
 * that rounded differently from the ledger would print a return that disagrees
 * with the books.
 */
exports.gstSummaryRowResponseSchema = zod_1.z.object({
    hsnSacCode: zod_1.z.string().nullable(),
    taxPercent: zod_1.z.number(),
    taxableAmountPaise: zod_1.z.number().int(),
    cgstAmountPaise: zod_1.z.number().int(),
    sgstAmountPaise: zod_1.z.number().int(),
    igstAmountPaise: zod_1.z.number().int(),
    taxAmountPaise: zod_1.z.number().int(),
});
/**
 * A posted bill with its lines — what `GET /op-billing/bills/:invoiceId` returns.
 *
 * The endpoint, its controller and `RegisterQueries.detail()` have all existed
 * since Phase 4C; only this declaration was missing, so `op-billing.operations`
 * referenced a `BillDetailResponse` schema that was never defined and the
 * register's View action had no typed client to call. Publishing it here is the
 * same "a real caller appeared" rule this file opens with.
 */
/**
 * The patient's reachable details, as the invoice prints them.
 *
 * A DISPLAY LABEL, not a snapshot — the same posture the register's own contract
 * already takes for `doctorName` and `departmentName`. Reprinting a six-month-old
 * bill for a patient who has moved should carry today's address, because that is
 * what makes the reprint usable; reprinting it with a different TOTAL would be
 * fraud. The two rules point opposite ways on purpose.
 */
exports.patientInvoiceContactResponseSchema = zod_1.z.object({
    mobile: zod_1.z.string().nullable(),
    /** Pre-assembled server-side, so the invoice prints one address rather than joining six nullable columns. */
    addressLines: zod_1.z.array(zod_1.z.string()),
    /**
     * The title printed before the patient's name — the one stored on the patient, else one derived from
     * gender and date of birth (a walk-in: the gender and age given at the counter). Null prints the name bare.
     * Optional so a response from a server older than this field still type-checks.
     */
    salutation: zod_1.z.string().nullable().optional(),
});
/**
 * How the bill was settled, for the "Paid By" cell — the View's own twin of
 * `PrintDocumentPayment` (`print.commands.ts`).
 *
 * Desk, 2026-09-11: "under op bill view payment mode isnot showing need print
 * and view of payment method which is user ig bank need to show refrence
 * number." The print path has always sent this; the View endpoint
 * (`GET /op-billing/bills/:invoiceId`) never computed it, so `TaxInvoiceDocument`
 * rendered its `PaymentCell` with `payments` undefined and printed "—" — the
 * exact shape of bug this file's own header already names for the
 * department/doctor line (`billDetailLineResponseSchema`'s note above): the
 * server had the answer, the View's schema just never declared where to put it.
 */
exports.billDetailPaymentResponseSchema = zod_1.z.object({
    /**
     * The `FinPaymentLine` row this display resolves from — added for the
     * Payment Correction Request workflow, which needs something to target a
     * correction at. Nullable only for defensiveness against a future
     * payment shape with no line at all; every real row has one.
     */
    paymentLineId: zod_1.z.string().uuid().nullable(),
    paymentId: zod_1.z.string().uuid().nullable(),
    mode: zod_1.z.string(),
    referenceNumber: zod_1.z.string().nullable(),
    amountPaise: zod_1.z.number().int(),
});
exports.billDetailResponseSchema = zod_1.z.object({
    invoice: exports.billRegisterRowResponseSchema,
    lines: zod_1.z.array(exports.billDetailLineResponseSchema),
    hsnSummary: zod_1.z.array(exports.gstSummaryRowResponseSchema),
    patientContact: exports.patientInvoiceContactResponseSchema,
    /** Empty for an unpaid bill — the same "Unpaid" cell the print path renders, not a blank. */
    payments: zod_1.z.array(exports.billDetailPaymentResponseSchema),
});
// ---------------------------------------------------------------------------
// Dashboard — seven read models, one per card (Dashboard Rule 2).
//
// EVERY FIGURE IS PAISE, INTEGER, AND COMPUTED BY SQL. The browser renders these
// and derives nothing from them: no ratio, no percentage, no total across cards.
// The frozen KPI definitions live in `OP-BILLING-PHASE-4C6-DASHBOARD-ACCEPTANCE.md`
// §2 and are enforced by the queries themselves.
// ---------------------------------------------------------------------------
/**
 * The KPI strip.
 *
 * REVENUE AND COLLECTED ARE DIFFERENT NUMBERS and both are reported. A credit
 * bill is revenue today and cash never; a payment against last week's bill is
 * cash today and revenue then. Conflating them is the commonest dashboard defect
 * in billing systems, which is why they are two fields rather than one.
 *
 * `averageBillPaise` is integer division, `0` when there are no bills — the
 * server does that division, not the browser.
 */
exports.opBillDashboardKpisResponseSchema = zod_1.z.object({
    date: zod_1.z.string(),
    billsToday: zod_1.z.number().int(),
    /** BILLED, not collected. Excludes CANCELLED and REVERSED. */
    revenueTodayPaise: zod_1.z.number().int(),
    /** COLLECTED, not billed. Excludes reversed and cancelled payments. */
    collectedTodayPaise: zod_1.z.number().int(),
    outstandingPaise: zod_1.z.number().int(),
    refundsTodayPaise: zod_1.z.number().int(),
    cancelledToday: zod_1.z.number().int(),
    averageBillPaise: zod_1.z.number().int(),
});
/** Billed revenue per day over a bounded window. */
exports.opBillRevenueTrendPointSchema = zod_1.z.object({
    date: zod_1.z.string(),
    revenuePaise: zod_1.z.number().int(),
    billCount: zod_1.z.number().int(),
});
/** Collections grouped by payment mode. The group total equals Collected. */
exports.opBillPaymentMixSliceSchema = zod_1.z.object({
    paymentModeId: zod_1.z.string(),
    paymentMode: zod_1.z.string(),
    amountPaise: zod_1.z.number().int(),
});
/** Bills per hour — the counter-staffing view. */
exports.opBillHourlyPointSchema = zod_1.z.object({
    hour: zod_1.z.number().int(),
    billCount: zod_1.z.number().int(),
    revenuePaise: zod_1.z.number().int(),
});
/** Most-billed services, read from invoice-line SNAPSHOTS rather than the catalogue. */
exports.opBillTopServiceSchema = zod_1.z.object({
    /** `SERV CODE` + `SERV NUMBER` as billed (ADR-083), falling back to the pre-Service-Master charge code. */
    chargeCode: zod_1.z.string(),
    /** `SERV NAME` — the INVOICE name, the string printed on the patient's bill. */
    chargeName: zod_1.z.string(),
    revenuePaise: zod_1.z.number().int(),
    quantity: zod_1.z.number(),
    /**
     * False when the line carries no `SERV CODE` — a service billed before the
     * Service Master existed, or one with no Account Head.
     *
     * Sent rather than inferred from the code's shape, so the UI can MARK the row
     * instead of presenting a legacy charge code as though it were a service.
     * Those rows are never dropped: on the live tenant they are most of the day's
     * revenue, and hiding them would make this card disagree with the revenue KPI.
     */
    isClassified: zod_1.z.boolean(),
});
/** Billed revenue grouped by department. */
exports.opBillDepartmentRevenueSchema = zod_1.z.object({
    departmentId: zod_1.z.string().nullable(),
    departmentName: zod_1.z.string(),
    revenuePaise: zod_1.z.number().int(),
});
/**
 * Billed revenue grouped by doctor.
 *
 * `doctorId` references `User`, not `Doctor` — joining `Doctor.id` matches zero
 * rows and fails silently as a blank column.
 */
exports.opBillDoctorRevenueSchema = zod_1.z.object({
    doctorId: zod_1.z.string().nullable(),
    doctorName: zod_1.z.string(),
    revenuePaise: zod_1.z.number().int(),
    billCount: zod_1.z.number().int(),
});
/**
 * One priced line of a draft — the pre-post breakdown.
 *
 * `rateSource` and `rateType` answer "why did this line cost that?" — a
 * corporate tariff, a package allowance, the standard rate. That is the first
 * question a patient asks and the one a cashier previously could not answer.
 */
exports.pricedLineResponseSchema = zod_1.z.object({
    lineRef: zod_1.z.string().nullable(),
    chargeId: zod_1.z.string(),
    chargeCode: zod_1.z.string().nullable(),
    description: zod_1.z.string(),
    quantity: zod_1.z.number(),
    unitRatePaise: zod_1.z.number().int(),
    grossAmountPaise: zod_1.z.number().int(),
    discountPercent: zod_1.z.number().nullable(),
    discountAmountPaise: zod_1.z.number().int(),
    taxPercent: zod_1.z.number(),
    taxAmountPaise: zod_1.z.number().int(),
    netAmountPaise: zod_1.z.number().int(),
    rateSource: zod_1.z.string(),
    rateType: zod_1.z.string(),
    /**
     * Whether the counter fills this line's Rate: an account head, or a catalogue
     * service held at the ₹1 open-value sentinel.
     *
     * The SERVER says which lines these are. The browser must not re-derive it
     * from `unitRatePaise === 100`, or the rule would exist twice and the screen
     * could offer an edit the server then refuses. Defaulted so an older response
     * (or a fixture written before this field) reads as "not editable" rather
     * than failing the parse.
     */
    rateEditable: zod_1.z.boolean().default(false),
    /** An editable line still at its placeholder. The post button stays disabled. */
    rateMissing: zod_1.z.boolean().default(false),
    /**
     * DISPLAY labels, not money. Present so a bill RESUMED from a draft can show
     * which department each service belongs to — a draft stores intent (a
     * chargeId and a quantity) and no labels at all, so without these the
     * Department column on a resumed bill reads "—" on every row.
     */
    departmentId: zod_1.z.string().nullable(),
    /**
     * `servCode` + `servNumber` — the operator-facing identifier (`XR001`).
     *
     * CARRIED FOR THE SAME REASON `departmentId` IS: a draft stores intent — a
     * chargeId and a quantity — and no labels, so a RESUMED bill has nothing to
     * classify its own lines by. Without it the counter cannot tell which rows
     * are radiology, and the per-line category control (desk, 2026-09-09) would
     * silently vanish on exactly the bills most likely to need it: the ones
     * parked and picked up later.
     *
     * Null on an account head line, and on a service created before ADR-083
     * allocated identifiers.
     */
    identifier: zod_1.z.string().nullable(),
    departmentName: zod_1.z.string().nullable(),
});
/**
 * The priced draft. READ-ONLY — nothing is persisted and no number is drawn.
 *
 * THESE FIGURES ARE NOT A QUOTE THE SERVER IS BOUND TO. `postBill` re-resolves
 * everything (§C5 step 2), because a tariff can activate, a package can be
 * consumed and a discount approval can land between the preview and the post.
 * It is a faithful "what it costs right now" — which is exactly why posting
 * re-prices rather than trusting it.
 *
 * The GST split is carried in full because the place of supply decides whether
 * it is CGST+SGST or IGST, and a cashier asked "why is there no IGST?" needs the
 * answer on screen rather than in a report.
 */
exports.priceBillResponseSchema = zod_1.z.object({
    lines: zod_1.z.array(exports.pricedLineResponseSchema),
    grossAmountPaise: zod_1.z.number().int(),
    discountAmountPaise: zod_1.z.number().int(),
    taxableAmountPaise: zod_1.z.number().int(),
    taxAmountPaise: zod_1.z.number().int(),
    cgstAmountPaise: zod_1.z.number().int(),
    sgstAmountPaise: zod_1.z.number().int(),
    igstAmountPaise: zod_1.z.number().int(),
    cessAmountPaise: zod_1.z.number().int(),
    roundOffAmountPaise: zod_1.z.number().int(),
    netAmountPaise: zod_1.z.number().int(),
    warnings: zod_1.z.array(zod_1.z.object({ code: zod_1.z.string(), message: zod_1.z.string(), severity: zod_1.z.string().optional() }).passthrough()),
});
/**
 * Employee / Company Credit Ledger — what a named party still owes from OP
 * bills posted on credit.
 *
 * Every figure is the SERVER's, read from the invoice rather than stored on the
 * credit entry, so a bill that is later collected or cancelled falls off this
 * ledger on its own. `runningOutstandingPaise` is oldest-bill-first, which is
 * how a ledger is read.
 */
exports.opBillCreditLedgerEntrySchema = zod_1.z.object({
    invoiceId: zod_1.z.string().uuid(),
    invoiceNumber: zod_1.z.string(),
    invoiceDate: zod_1.z.string(),
    patientId: zod_1.z.string().uuid(),
    netAmountPaise: zod_1.z.number().int(),
    amountPaidPaise: zod_1.z.number().int(),
    amountOutstandingPaise: zod_1.z.number().int(),
    status: zod_1.z.string(),
    runningOutstandingPaise: zod_1.z.number().int(),
    createdAt: zod_1.z.string(),
});
exports.opBillCreditLedgerResponseSchema = zod_1.z.object({
    entries: zod_1.z.array(exports.opBillCreditLedgerEntrySchema),
    totalOutstandingPaise: zod_1.z.number().int(),
});
/** One row per party with OP credit bills — the ledger LIST view. */
exports.opBillCreditBalanceSchema = zod_1.z.object({
    partyId: zod_1.z.string().uuid(),
    billCount: zod_1.z.number().int(),
    totalOutstandingPaise: zod_1.z.number().int(),
});
// ---------------------------------------------------------------------------
// Drafts — a bill saved or parked before it is posted.
//
// MIRRORS `DraftResponse` in the backend's `op-bill.contracts.ts`. Declared
// here because it is a SHARED shape and a feature file restating it is exactly
// what acceptance criterion A8 fails the build on: the Hold Bills list, the
// counter's resume path and the backend all have to agree about what a draft
// is, and three copies of that agreement is two too many.
// ---------------------------------------------------------------------------
/**
 * One stored line of INTENT — no name and no price.
 *
 * A name belongs to the catalogue, which may rename a service between parking a
 * bill and resuming it, and the resumed bill must read as the service is called
 * TODAY. A price belongs to the server, re-resolved at post (§C5 step 2). So a
 * draft keeps neither, and resuming re-prices to recover both.
 */
exports.opBillDraftLineResponseSchema = zod_1.z.object({
    chargeId: zod_1.z.string().optional(),
    accountHeadId: zod_1.z.string().optional(),
    /** Paise, on an ACCOUNT HEAD line only — the operator's own typed intent. */
    unitRatePaise: zod_1.z.number().int().optional(),
    description: zod_1.z.string().optional(),
    quantity: zod_1.z.number().optional(),
    lineRef: zod_1.z.string().optional(),
    doctorId: zod_1.z.string().optional(),
    /**
     * THIS LINE'S OWN CATEGORY — radiology only (desk, 2026-09-09).
     *
     * ECHOED BACK BECAUSE IT IS THE OPERATOR'S INTENT, which is exactly what a
     * draft keeps. The note above explains why a draft stores no name and no
     * price — both belong elsewhere and are recovered by re-pricing — but a
     * per-line category is neither: nothing else in the system knows the operator
     * chose C3 for that X-ray. Omitting it here would let a parked bill come back
     * silently priced at the bill's category, on a screen whose whole promise is
     * that a parked bill returns as it was left.
     */
    chargeTier: zod_1.z.enum(['C1', 'C2', 'C3', 'C4']).optional(),
    remarks: zod_1.z.string().optional(),
});
exports.opBillDraftResponseSchema = zod_1.z.object({
    draftId: zod_1.z.string(),
    /** `CONVERTED` and `DISCARDED` are terminal — a draft is editable until it is one of them. */
    status: zod_1.z.enum(['DRAFT', 'HELD', 'CONVERTED', 'DISCARDED']),
    /** CAS guard. A mismatch is 409, never a silent merge (ADR-063). */
    lockVersion: zod_1.z.number().int(),
    patientId: zod_1.z.string().nullable(),
    /**
     * THE OTHER HALF OF "WHO IS THIS BILL FOR".
     *
     * A walk-in has no `patientId`; the name the operator typed IS the party, and
     * the server has stored and returned it all along. This schema simply never
     * declared it, so a resumed walk-in bill came back nameless and the desk had
     * to retype a name the draft already held.
     *
     * Null on a patient bill, where the patient record owns the name.
     */
    walkInName: zod_1.z.string().nullable(),
    walkInMobile: zod_1.z.string().nullable(),
    walkInGender: zod_1.z.string().nullable(),
    walkInAgeYears: zod_1.z.number().int().nullable(),
    /** The header a RESUME restores — without these a resumed bill cannot be posted. */
    branchId: zod_1.z.string(),
    counterId: zod_1.z.string().nullable(),
    doctorId: zod_1.z.string().nullable(),
    holdReason: zod_1.z.string().nullable(),
    /**
     * The invoice this draft became, once `status` is `CONVERTED`; null before.
     *
     * What lets a screen that never received the post's answer (the connection
     * dropped after the server committed) find out whether the bill exists WITHOUT
     * posting again: read the draft, and if it names an invoice the bill is real.
     * Additive and optional, so an older client simply ignores it.
     */
    convertedInvoiceId: zod_1.z.string().nullable().optional(),
    /** Indicative only — NEVER authoritative. Display it as an estimate or not at all. */
    estimatedNetPaise: zod_1.z.number().int(),
    lines: zod_1.z.array(exports.opBillDraftLineResponseSchema),
    createdAt: zod_1.z.string(),
    updatedAt: zod_1.z.string(),
});
/**
 * STAFF DISCOUNT — what the counter is shown before it commits.
 *
 * `value` is the administrator's figure (whole percent, or paise, per
 * `calculationMethod`). It is DISPLAYED and never sent back: applying the
 * discount transmits an employee id alone, so there is no way for the desk to
 * alter what comes off the bill.
 *
 * `ruleId` is NULL for exactly one offer: the Consultation Fee dialog's
 * C.M.O. full waiver, which names no administrator-set `FinDiscountRule` row
 * because it is a fact about one doctor rather than a configured rule — see
 * `StaffDiscountService.findConsultationOffer`. Never shown to the operator
 * either way; it exists for the Discount Register to trace an offer back to
 * its rule, and a waiver has none to trace to.
 */
exports.staffDiscountOfferSchema = zod_1.z.object({
    ruleId: zod_1.z.string().nullable(),
    ruleCode: zod_1.z.string(),
    name: zod_1.z.string(),
    calculationMethod: zod_1.z.enum(['PERCENTAGE', 'FLAT_AMOUNT']),
    value: zod_1.z.number().int(),
    maxDiscountAmountPaise: zod_1.z.number().int().nullable(),
});
/** Null when the head carries no staff rule — an ordinary answer, not an error. */
exports.staffDiscountOfferResponseSchema = zod_1.z.object({ offer: exports.staffDiscountOfferSchema.nullable() });
exports.staffDiscountAppliedResponseSchema = zod_1.z.object({
    linesDiscounted: zod_1.z.number().int().optional(),
    cleared: zod_1.z.number().int().optional(),
});
//# sourceMappingURL=op-billing.response.js.map